import { useEffect, useState, type JSX } from 'react';
import { api, type TelegramBot } from '../api.js';
import { Icon } from './Icon.js';
import { isolate } from '../utils/bidi.js';

/**
 * Боты выдачи билетов в настройках профиля. Отдельная карточка со своими
 * запросами: это не поля профиля, а свой список. Снаружи формы профиля —
 * у карточки свои кнопки, а форма в форме в HTML запрещена.
 *
 * Токен приходит замаскированным («••••» + 4 символа) и в PATCH не
 * отправляется, пока его не заменили целиком.
 */

const ERRORS: Record<string, string> = {
  invalid_token: 'Это не похоже на токен BotFather: нужно «<цифры>:<35 символов>».',
  token_rejected: 'Telegram не принял токен — проверь его в BotFather.',
  bot_token_taken: 'Бот с таким токеном уже добавлен (в этом или другом профиле).',
  telegram_unreachable: 'Telegram не отвечает — попробуй позже.',
  not_found: 'Профиль не найден или нет доступа.',
  invalid_support_chat: 'Не разобрали чат поддержки: нужна ссылка вида https://t.me/c/…/… или id группы.',
  support_chat_unreachable: 'Бот не видит чат поддержки — добавь его в группу.',
};

function errText(e: unknown): string {
  const d = (e as { details?: { error?: string; detail?: string } }).details;
  const code = d?.error ?? '';
  const base = ERRORS[code] ?? (e as Error).message ?? 'Ошибка';
  return d?.detail ? `${base} (${d.detail})` : base;
}

function fmt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '';
}

export function TelegramBotsCard({ profileId }: { profileId: string }): JSX.Element {
  const [bots, setBots] = useState<TelegramBot[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // id бота или 'new'
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newToken, setNewToken] = useState('');
  const [editing, setEditing] = useState<{
    id: string;
    name: string;
    token: string;
    supportChatId: string;
    supportThreadId: string;
  } | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const reload = (): void => {
    api
      .listProfileTelegramBots(profileId)
      .then(setBots)
      .catch((e: Error) => setErr(e.message));
  };
  useEffect(reload, [profileId]);

  const run = async (key: string, fn: () => Promise<void>): Promise<void> => {
    setBusy(key);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(errText(e));
    } finally {
      // Перечитываем и после ошибки: сервер мог сохранить бота, а упасть
      // позже — иначе в списке его нет, а повтор даёт «токен уже добавлен».
      reload();
      setBusy(null);
    }
  };

  const onAdd = (): Promise<void> =>
    run('new', async () => {
      const created = await api.createTelegramBot(profileId, { name: newName.trim(), token: newToken.trim() });
      setAdding(false);
      setNewName('');
      setNewToken('');
      if (created.canJoinGroups) {
        // Билеты в группу бот не выдаёт в любом случае — это только гигиена.
        setNote(
          `Бот @${created.username} может вступать в группы. Это нужно, только если подключаешь чат поддержки; иначе лучше отключить: BotFather → /setjoingroups → Disable.`,
        );
      }
    });

  const onSave = (): Promise<void> => {
    const e = editing!;
    return run(e.id, async () => {
      const token = e.token.startsWith('••••') ? undefined : e.token.trim() || undefined;
      await api.updateTelegramBot(e.id, {
        name: e.name.trim(),
        ...(token ? { token } : {}),
        supportChatId: e.supportChatId.trim(),
        supportThreadId: e.supportThreadId.trim(),
      });
      setEditing(null);
    });
  };

  const onDelete = (b: TelegramBot): Promise<void> | void => {
    if (!confirm(`Удалить бота «${isolate(b.name)}»? У продуктов, где он выбран, выдача через бот отключится.`)) return;
    return run(b.id, () => api.deleteTelegramBot(b.id));
  };

  return (
    <div className="card">
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="card-title">Telegram (выдача билетов)</div>
        {!adding && (
          <button type="button" className="btn btn-sm" onClick={() => setAdding(true)}>
            <Icon name="plus" size={13} /> Добавить бота
          </button>
        )}
      </div>
      <div className="card-body stack">
        <div style={{ fontSize: 12.5, color: 'var(--fg-subtle)' }}>
          Покупатель нажимает в боте <b>«Получить билет»</b> (или пишет /tickets), делится телефоном
          или присылает email из покупки и получает номера своих билетов. Бот выдаёт билеты только
          тех продуктов, где он выбран в блоке нумерации. Если задан чат поддержки, у бота есть кнопка{' '}
          <b>«Написать в поддержку»</b>: вопросы приходят в группу, ответ реплаем на сообщение бота
          уходит покупателю. Токен — из BotFather.
        </div>

        {err && (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--danger-fg)',
              background: 'var(--danger-subtle-bg)',
            }}
          >
            {err}
          </div>
        )}
        {note && (
          <div style={{ padding: 10, borderRadius: 8, fontSize: 13, background: 'var(--bg-subtle)' }}>
            {note}{' '}
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setNote(null)}>
              ок
            </button>
          </div>
        )}

        {adding && (
          <div className="stack" style={{ padding: 12, borderRadius: 8, background: 'var(--bg-subtle)' }}>
            <div className="field">
              <label className="field-label">Название</label>
              <input
                className="input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Билеты конференции"
              />
            </div>
            <div className="field">
              <label className="field-label">Токен бота</label>
              <input
                className="input mono"
                type="password"
                autoComplete="new-password"
                value={newToken}
                onChange={(e) => setNewToken(e.target.value)}
                placeholder="123456789:AAH…"
              />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy === 'new' || !newName.trim() || !newToken.trim()}
                onClick={() => void onAdd()}
              >
                {busy === 'new' ? 'Проверяем у Telegram…' : 'Добавить'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  setAdding(false);
                  setNewName('');
                  setNewToken('');
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        )}

        {bots === null && !err && <div style={{ fontSize: 13, color: 'var(--fg-subtle)' }}>Загрузка…</div>}
        {bots && bots.length === 0 && !adding && (
          <div style={{ fontSize: 13, color: 'var(--fg-subtle)' }}>Ботов пока нет.</div>
        )}

        {bots?.map((b) => {
          const ed = editing?.id === b.id ? editing : null;
          return (
            <div
              key={b.id}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid var(--border)',
                display: 'grid',
                gap: 8,
              }}
            >
              {ed ? (
                <>
                  <div className="field">
                    <label className="field-label">Название</label>
                    <input
                      className="input"
                      value={ed.name}
                      onChange={(e) => setEditing({ ...ed, name: e.target.value })}
                    />
                  </div>
                  <div className="field">
                    <label className="field-label">Токен бота</label>
                    <input
                      className="input mono"
                      type="password"
                      autoComplete="new-password"
                      value={ed.token}
                      onChange={(e) => setEditing({ ...ed, token: e.target.value })}
                    />
                    <div style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 4 }}>
                      Новый токен сервер проверит у Telegram и перерегистрирует вебхук.
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label">Чат поддержки</label>
                    <input
                      className="input mono"
                      value={ed.supportChatId}
                      onChange={(e) => setEditing({ ...ed, supportChatId: e.target.value })}
                      placeholder="https://t.me/c/3966465123/32"
                    />
                    <div style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 4 }}>
                      Ссылка на топик группы (Копировать ссылку) или id группы. Бот должен быть
                      участником группы. Пусто — без кнопки «Написать в поддержку».
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label">Топик</label>
                    <input
                      className="input mono"
                      value={ed.supportThreadId}
                      onChange={(e) => setEditing({ ...ed, supportThreadId: e.target.value })}
                      placeholder="32"
                    />
                    <div style={{ fontSize: 11, color: 'var(--fg-subtle)', marginTop: 4 }}>
                      Номер топика. Пусто — возьмём из ссылки; в группе без топиков не нужен.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={busy === b.id || !ed.name.trim()}
                      onClick={() => void onSave()}
                    >
                      Сохранить
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(null)}>
                      Отмена
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        <bdi>{b.name}</bdi>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                        {b.username ? (
                          <a href={`https://t.me/${b.username}`} target="_blank" rel="noreferrer">
                            @{b.username}
                          </a>
                        ) : (
                          'username неизвестен'
                        )}
                        <span className="mono" style={{ marginLeft: 10 }}>{b.token}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() =>
                          setEditing({
                            id: b.id,
                            name: b.name,
                            token: b.token,
                            supportChatId: b.supportChatId,
                            supportThreadId: b.supportThreadId,
                          })
                        }
                        title="Название, токен, чат поддержки"
                      >
                        <Icon name="pencil" size={13} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost btn-danger"
                        disabled={busy === b.id}
                        onClick={() => void onDelete(b)}
                        title="Удалить бота"
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {b.webhookSetAt ? (
                      <span className="chip no-dot chip-ok">вебхук зарегистрирован {fmt(b.webhookSetAt)}</span>
                    ) : (
                      <span className="chip no-dot chip-neutral" style={{ color: 'var(--danger-fg)' }}>
                        вебхук не зарегистрирован{b.webhookError ? `: ${b.webhookError}` : ''}
                      </span>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      disabled={busy === b.id}
                      onClick={() => void run(b.id, () => api.registerTelegramBotWebhook(b.id).then(() => undefined))}
                    >
                      <Icon name="refresh" size={12} /> Зарегистрировать заново
                    </button>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                    {b.supportChatId ? (
                      <>
                        Поддержка: чат <span className="mono">{b.supportChatId}</span>
                        {b.supportThreadId ? (
                          <>
                            , топик <span className="mono">{b.supportThreadId}</span>
                          </>
                        ) : null}
                      </>
                    ) : (
                      'Поддержка не подключена — кнопки «Написать в поддержку» в боте нет.'
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
